import { ChevronRight, X } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Link, NavLink } from 'react-router'

import { cn } from '../../utils/cn'
import { Logo } from '../brand/Logo'
import { Button } from '../ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import type { SidebarIcon } from './model'
import { SIDEBAR_ROW } from './SidebarNavLink'

/** A product heads its pages: a taller row with larger text than the pages under it. */
const PRODUCT_ROW = 'h-10 cursor-pointer justify-start text-md font-normal has-[>svg]:px-2'

export interface DrawerPage {
  title: string
  /** Absolute for another app's page, otherwise a route in this one. */
  url: string
  exact?: boolean
}

export interface DrawerProduct<T extends string> {
  value: T
  label: string
  icon: SidebarIcon
  /** Its pages; with none, the product is a single row that runs `onSelect`. */
  pages: DrawerPage[]
  onSelect?: () => void
}

interface ProductNavDrawerProps<T extends string> {
  products: DrawerProduct<T>[]
  /** The open product, which starts expanded. */
  active: T
  /** Where the logo leads. */
  home: { href: string; label: string }
  /** Closes the drawer once something is picked. */
  onNavigate: () => void
  /** Rows of another kind after the products, such as staff's console switch. */
  children?: ReactNode
}

/** Every Cai app's phone menu: the logo, then each product as a group that opens onto its pages. */
export function ProductNavDrawer<T extends string>({
  products,
  active,
  home,
  onNavigate,
  children,
}: ProductNavDrawerProps<T>) {
  const [expanded, setExpanded] = useState<T | null>(active)

  return (
    // Scrolls on its own: the sheet is viewport-high and an open group can outgrow a short phone.
    <div className="no-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="flex items-center justify-between pt-3 pr-2 pb-1">
        <Link
          to={home.href}
          aria-label={home.label}
          onClick={onNavigate}
          className="inline-flex px-4 py-2 leading-0"
        >
          <Logo theme="black" size="small" width={64} />
        </Link>
        {/* The sheet's own close button is hidden for this layout, so the drawer carries a labelled one. */}
        <Button
          variant="ghost"
          size="icon"
          aria-label="Close menu"
          onClick={onNavigate}
          className="text-ink-600 size-7"
        >
          <X />
        </Button>
      </div>
      <nav aria-label="Cai products" className="text-ink-600 flex flex-col gap-1 p-2">
        {products.map(({ value, label, icon: Icon, pages, onSelect }) => {
          if (pages.length === 0) {
            return (
              <Button
                key={value}
                variant="ghost"
                aria-current={value === active ? 'page' : undefined}
                onClick={() => {
                  onSelect?.()
                  onNavigate()
                }}
                className={cn(SIDEBAR_ROW, PRODUCT_ROW)}
              >
                <Icon aria-hidden="true" />
                <span>{label}</span>
              </Button>
            )
          }

          return (
            <Collapsible
              key={value}
              open={expanded === value}
              onOpenChange={(open) => setExpanded(open ? value : null)}
            >
              <CollapsibleTrigger className={cn(SIDEBAR_ROW, PRODUCT_ROW, 'group/product')}>
                <Icon aria-hidden="true" />
                <span>{label}</span>
                <ChevronRight
                  aria-hidden="true"
                  className="ml-auto transition-transform group-data-[state=open]/product:rotate-90 motion-reduce:transition-none"
                />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <ul className="flex flex-col gap-0.5 py-1 pl-8">
                  {pages.map((page) => (
                    <li key={page.url}>
                      <DrawerPageRow page={page} onNavigate={onNavigate} />
                    </li>
                  ))}
                </ul>
              </CollapsibleContent>
            </Collapsible>
          )
        })}
        {children}
      </nav>
    </div>
  )
}

function DrawerPageRow({ page, onNavigate }: { page: DrawerPage; onNavigate: () => void }) {
  // Another app's page is a full page load, and never the page we are on.
  if (page.url.startsWith('http')) {
    return (
      <a href={page.url} className={SIDEBAR_ROW}>
        <span>{page.title}</span>
      </a>
    )
  }
  return (
    <NavLink to={page.url} end={page.exact} onClick={onNavigate} className={SIDEBAR_ROW}>
      <span>{page.title}</span>
    </NavLink>
  )
}
